"use client";

import {useCallback, useEffect, useId, useRef, useState} from "react";
import {useGailLauncher, type LauncherPrompt} from "@/contexts/chat/GailLauncherContext";
import {useStableCallback} from "@/hooks/useStableCallback";
import {useGailLauncherShortcut} from "@/hooks/useGailLauncherShortcut";

/** Conversation state shared by either launcher placement. */
export function useLauncherConversation(onOpen?: (source: "launcher" | "tip") => void) {
  const [open, setOpen] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [pendingPrompt, setPendingPrompt] = useState<LauncherPrompt | null>(null);
  const [keyboardInteraction, setKeyboardInteraction] = useState(false);
  const panelId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    // Capture runs before preview, sheet, or page-card handlers change visibility.
    // Tracking both inputs also handles Escape after a pointer-opened sheet.
    const onKeyDown = () => setKeyboardInteraction(true);
    const onPointerDown = () => setKeyboardInteraction(false);
    window.addEventListener("keydown", onKeyDown, true);
    window.addEventListener("pointerdown", onPointerDown, true);
    return () => {
      window.removeEventListener("keydown", onKeyDown, true);
      window.removeEventListener("pointerdown", onPointerDown, true);
    };
  }, []);

  const openSheet = useStableCallback((prompt?: LauncherPrompt, source: "launcher" | "tip" = "launcher") => {
    onOpen?.(source);
    if (prompt) {
      setConversationId(null);
      setPendingPrompt(prompt);
    }
    setOpen(true);
  });
  const closeSheet = useCallback(() => {
    setOpen(false);
    // A prompt that never found an assistant must not fire on a later open.
    setPendingPrompt(null);
  }, []);
  const {registerLauncher} = useGailLauncher();
  useEffect(() => {
    registerLauncher(openSheet);
    return () => registerLauncher(null);
  }, [registerLauncher, openSheet]);
  useGailLauncherShortcut(() => open ? closeSheet() : openSheet());

  return {
    open, openSheet, closeSheet, triggerRef, panelId, keyboardInteraction, setKeyboardInteraction,
    sheetProps: {
      id: panelId, triggerRef, open, instantMotion: keyboardInteraction, onClose: closeSheet,
      conversationId, onSelectConversation: setConversationId, onNewChat: () => setConversationId(null),
      initialPrompt: pendingPrompt, onInitialPromptHandled: () => setPendingPrompt(null),
    },
  };
}
